import { useState, useEffect, useRef } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';
import OrbisSpinner from '@/components/ui/orbis-spinner';

const STEPS = [
  'Connecting to server...',
  'Fetching page HTML...',
  'Scanning color palette...',
  'Reading typography...',
  'Detecting layout sections...',
  'AI-powered refinement...',
];

const STEP_DELAYS = [0, 1500, 3500, 5500, 7500, 10000];

const S = {
  block: { background: '#2A2A2A', borderRadius: '4px' },
  blockLight: { background: '#222222', borderRadius: '4px' },
};

function LiveTimer({ startTime }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setElapsed(Date.now() - startTime), 100);
    return () => clearInterval(iv);
  }, [startTime]);
  const s = Math.floor(elapsed / 1000);
  const m = Math.floor(s / 60);
  return (
    <span style={{ fontSize: '11px', fontFamily: 'var(--orbis-mono)', color: '#5A5855' }}>
      {m > 0 ? `${m}:${String(s % 60).padStart(2, '0')}` : `0:${String(s).padStart(2, '0')}`}
    </span>
  );
}

function SkeletonFallback() {
  return (
    <div className="flex flex-col" style={{ padding: '0' }}>
      {/* Nav */}
      <div className="flex items-center justify-between px-6" style={{ height: '52px', background: '#1A1A1A', borderBottom: '0.5px solid rgba(255,255,255,0.05)' }}>
        <div style={{ ...S.block, width: '80px', height: '20px' }} className="animate-pulse" />
        <div className="flex items-center gap-4">
          {[60, 52, 64, 48].map((w, i) => (
            <div key={i} style={{ ...S.blockLight, width: `${w}px`, height: '10px', animationDelay: `${i * 0.1}s` }} className="animate-pulse" />
          ))}
        </div>
        <div style={{ ...S.block, width: '88px', height: '32px', borderRadius: '6px', animationDelay: '0.4s' }} className="animate-pulse" />
      </div>
      {/* Hero */}
      <div className="flex flex-col items-center justify-center gap-4" style={{ height: '200px', background: '#181818', borderBottom: '0.5px solid rgba(255,255,255,0.04)', padding: '32px' }}>
        <div style={{ ...S.block, width: '55%', height: '28px', animationDelay: '0.1s' }} className="animate-pulse" />
        <div style={{ ...S.blockLight, width: '45%', height: '28px', animationDelay: '0.2s' }} className="animate-pulse" />
        <div className="flex gap-3 mt-2">
          <div style={{ ...S.block, width: '100px', height: '36px', borderRadius: '6px', animationDelay: '0.3s' }} className="animate-pulse" />
          <div style={{ ...S.blockLight, width: '80px', height: '36px', borderRadius: '6px', animationDelay: '0.4s' }} className="animate-pulse" />
        </div>
      </div>
      {/* Features */}
      <div className="grid gap-4 px-6 py-6" style={{ gridTemplateColumns: 'repeat(3, 1fr)', background: '#161616', borderBottom: '0.5px solid rgba(255,255,255,0.04)' }}>
        {[0, 1, 2].map(i => (
          <div key={i} className="flex flex-col gap-2.5 p-4" style={{ background: '#1C1C1C', borderRadius: '8px', animationDelay: `${i * 0.15}s` }}>
            <div style={{ ...S.block, width: '36px', height: '36px', borderRadius: '8px' }} className="animate-pulse" />
            <div style={{ ...S.block, width: '70%', height: '12px', marginTop: '4px', animationDelay: `${0.1 + i * 0.1}s` }} className="animate-pulse" />
            <div style={{ ...S.blockLight, width: '90%', height: '9px', animationDelay: `${0.2 + i * 0.1}s` }} className="animate-pulse" />
            <div style={{ ...S.blockLight, width: '75%', height: '9px', animationDelay: `${0.3 + i * 0.1}s` }} className="animate-pulse" />
          </div>
        ))}
      </div>
      {/* CTA */}
      <div className="flex items-center justify-center gap-6" style={{ height: '80px', background: '#1A1A1A' }}>
        <div style={{ ...S.blockLight, width: '220px', height: '16px', animationDelay: '0.2s' }} className="animate-pulse" />
        <div style={{ ...S.block, width: '110px', height: '36px', borderRadius: '6px', animationDelay: '0.35s' }} className="animate-pulse" />
      </div>
    </div>
  );
}

export default function CloneScanningView({ url, isDone = false, screenshotUrl = null }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [startTime] = useState(Date.now());
  const [iframeBlocked, setIframeBlocked] = useState(false);
  const iframeRef = useRef(null);

  const fullUrl = url
    ? (url.startsWith('http') ? url : `https://${url}`)
    : null;

  const displayUrl = url
    ? url.replace(/^https?:\/\//, '').replace(/\/$/, '')
    : 'Analyzing...';

  useEffect(() => {
    if (isDone) { setStepIndex(STEPS.length - 1); return; }
    const timers = STEP_DELAYS.slice(1).map((delay, i) =>
      setTimeout(() => setStepIndex(i + 1), delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [isDone]);

  // Fallback to skeleton if iframe fails to load (X-Frame-Options) after 6s
  // Once iframe is confirmed loaded, never revert to skeleton
  const iframeLoadedRef = useRef(false);
  useEffect(() => {
    if (!fullUrl || iframeLoadedRef.current) return;
    const t = setTimeout(() => {
      if (iframeLoadedRef.current) return; // already confirmed loaded
      try {
        const doc = iframeRef.current?.contentDocument;
        if (!doc || !doc.body || doc.body.childNodes.length === 0) {
          setIframeBlocked(true);
        } else {
          iframeLoadedRef.current = true; // confirmed loaded, never revert
        }
      } catch {
        // Cross-origin means it loaded — mark as confirmed
        iframeLoadedRef.current = true;
      }
    }, 6000);
    return () => clearTimeout(t);
  }, [fullUrl]);

  return (
    <div className="flex flex-col h-full" style={{ background: '#161616' }}>
      {/* Browser chrome */}
      <div
        className="flex items-center gap-3 flex-shrink-0 px-4"
        style={{ height: '40px', background: '#1A1A1A', borderBottom: '0.5px solid rgba(255,255,255,0.08)' }}
      >
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#FF5F57' }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#FFBD2E' }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#28C840' }} />
        </div>
        <div
          className="flex-1 flex items-center px-3"
          style={{ background: '#222222', borderRadius: '6px', height: '26px', maxWidth: '400px', border: '0.5px solid rgba(255,255,255,0.08)' }}
        >
          <span style={{ fontSize: '12px', fontWeight: 300, color: '#8A8882', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayUrl}
          </span>
        </div>
      </div>

      {/* Scanning area */}
      <div className="flex-1 relative overflow-hidden">
        {/* Scan line */}
        {!isDone && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, transparent 0%, var(--orbis-blue) 50%, transparent 100%)',
              boxShadow: '0 0 40px 12px rgba(184,204,223,0.2)',
              animation: 'clone-scan-sweep 2.8s linear infinite',
              zIndex: 20,
              top: 0,
            }}
          />
        )}

        {/* Dark scan overlay — dims the site slightly to give scanning feel */}
        {fullUrl && !iframeBlocked && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(14,14,14,0.15)',
              zIndex: 5,
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Reference preview: prefer Crawl4AI screenshot, then live iframe, then skeleton */}
        {screenshotUrl ? (
          <img
            src={screenshotUrl}
            alt="Reference website screenshot"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block', pointerEvents: 'none' }}
          />
        ) : fullUrl && !iframeBlocked ? (
          <iframe
            ref={iframeRef}
            src={fullUrl}
            className="w-full h-full"
            style={{ border: 'none', display: 'block', pointerEvents: 'none' }}
            title="Reference site"
            sandbox="allow-scripts allow-same-origin"
            onError={() => setIframeBlocked(true)}
          />
        ) : (
          <div style={{ background: '#161616', height: '100%', overflowY: 'auto' }}>
            <SkeletonFallback />
          </div>
        )}
      </div>

      {/* Step status bar */}
      <div
        className="flex items-center justify-between flex-shrink-0 px-5"
        style={{ height: '44px', background: '#1A1A1A', borderTop: '0.5px solid rgba(255,255,255,0.07)' }}
      >
        <div className="flex items-center gap-2.5">
          {isDone
            ? <CheckCircle2 className="w-3.5 h-3.5" style={{ color: 'var(--orbis-green)' }} />
            : <OrbisSpinner size={14} color="var(--orbis-blue)" />
          }
          <span style={{ fontSize: '13px', fontWeight: 300, color: isDone ? 'var(--orbis-green)' : 'var(--orbis-warm)' }}>
            {isDone ? 'Analysis complete' : STEPS[Math.min(stepIndex, STEPS.length - 1)]}
          </span>
        </div>
        <LiveTimer startTime={startTime} />
      </div>

      <style>{`
        @keyframes clone-scan-sweep {
          0%   { top: 0%; opacity: 0; }
          5%   { opacity: 1; }
          95%  { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
      `}</style>
    </div>
  );
}
